import { useBrokerContext } from '@/app/context';
import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { useRequirePermissions } from '@/shared/hooks';
import { Button, Tooltip } from '@noorden-insurtech/alba-design-system';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { Home, Plus } from 'lucide-react';
import { QuotesContent } from '../../../features/quotes/components';
import { Page, PageHeader } from '../../../shared/ui/page';
import { requirePermissions } from '../../../shared/utils';

export const Route = createFileRoute('/_app/quotes/')({
  component: RouteComponent,
  beforeLoad: async () => {
    const hasPermission = await requirePermissions(['policies.quotes.read']);
    if (!hasPermission) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function RouteComponent() {
  const { selectedBroker } = useBrokerContext();
  const hasBrokerSelected = selectedBroker !== null;
  const { hasPermission: canCreateQuote } = useRequirePermissions({
    permissions: ['policies.quotes.write'],
  });
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const isCreateDisabled = !hasBrokerSelected || quoteWriteGuard.blocked;

  const buttonContent = (
    <Link to='/quotes/products' disabled={isCreateDisabled}>
      <Button size='md' disabled={isCreateDisabled}>
        <Plus className='h-4 w-4' />
        Nova Cotação
      </Button>
    </Link>
  );

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Cotações', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Cotações</PageHeader.Title>
            <PageHeader.Subtitle>
              Gerencie as cotações dos seus clientes
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            {canCreateQuote && (
              !hasBrokerSelected ? (
                <Tooltip.Root positioning={{ placement: 'top' }}>
                  <Tooltip.Trigger asChild>
                    <span className='inline-block'>{buttonContent}</span>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      Selecione um corretor para habilitar a nova cotação.
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              ) : (
                <span className='inline-block' title={quoteWriteGuard.reason}>
                  {buttonContent}
                </span>
              )
            )}
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>
      <BrokerInactiveNotice guards={[quoteWriteGuard]} />
      <QuotesContent selectedBroker={selectedBroker} />
    </Page.Root>
  );
}
